import React, { useEffect, useMemo, useState } from "react";
import FiltroMultiple from "../../../Postventa/Dashboard/Modales/FiltroMultiple";

const SEGMENTOS = [
  { key: "pequeño_productor", label: "Pequeño Productor" },
  { key: "mediano_productor", label: "Mediano Productor" },
  { key: "gran_productor", label: "Gran Productor" },
  { key: "estrategico", label: "Estrategico" },
  { key: "clave", label: "Clave" },
  { key: "sin_cultivos", label: "Sin Cultivos" },
  { key: "sin_clasificar", label: "Sin Clasificar" },
];

const porcentaje = (visitado, asignado) => (asignado > 0 ? `${Math.min(100, Math.round((visitado / asignado) * 100))}%` : "—");

// Tabla de Cobertura de Cartera — por asesor, 3 sub-filas fijas (Cartera asignada / Visitada /
// %), sin colapsar (los asesores siempre están visibles, a diferencia de los demás reportes).
// "listado" llega plano desde ReportesCRM/ReporteCoberturaCartera — cada fila es un asesor con
// idasesor, asesor, idsucursal, sucursal, clientes_asignados, clientes_visitados_total y, por
// cada SEGMENTOS, {segmento}_asignados/{segmento}_visitados. sin_visitar y porcentaje_cobertura
// se calculan aquí (asignado - visitado / visitado sobre asignado).
const ESTADO_VACIO_FILTROS = { asesor: [] };
const ESTADO_VACIO_MOSTRAR = { asesor: { open: false } };

export const ReporteCoberturaCarteraTabla = ({ listado: listadoProp }) => {
  const listadoCompleto = Array.isArray(listadoProp) ? listadoProp : [];
  const [options, setOptions] = useState(ESTADO_VACIO_FILTROS);
  const [filtros, setFiltros] = useState(ESTADO_VACIO_FILTROS);
  const [filtrosTemp, setFiltrosTemp] = useState(ESTADO_VACIO_FILTROS);
  const [mostrarFiltro, setMostrarFiltro] = useState(ESTADO_VACIO_MOSTRAR);
  const [prioridadFiltros, setPrioridadFiltros] = useState([]);

  useEffect(() => {
    setOptions({
      asesor: [...new Set(listadoCompleto.map((a) => a.asesor).filter(Boolean))].map((v) => ({ value: v, label: v })),
    });
  }, [listadoCompleto]);

  const actualizarPrioridad = (filtro) => {
    setPrioridadFiltros((prev) => {
      const index = prev.indexOf(filtro);
      const nuevasPrioridades = index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);

      setFiltrosTemp((prevTemp) => {
        const limpio = { ...prevTemp };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      setFiltros((prevFiltros) => {
        const limpio = { ...prevFiltros };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      return nuevasPrioridades;
    });
  };

  const listado = useMemo(() => {
    if (filtros.asesor.length === 0) return listadoCompleto;
    return listadoCompleto.filter((a) => filtros.asesor.includes(a.asesor));
  }, [listadoCompleto, filtros]);

  const filtroProps = (field, label) => ({
    label,
    field,
    options: options[field],
    filtros,
    setFiltros,
    filtrosTemp,
    setFiltrosTemp,
    mostrarFiltro,
    setMostrarFiltro,
    actualizarPrioridad,
    setPrioridadFiltros,
  });

  const totales = listado.reduce(
    (acc, a) => {
      SEGMENTOS.forEach(({ key }) => {
        acc[`${key}_asignados`] = (acc[`${key}_asignados`] || 0) + (a[`${key}_asignados`] || 0);
        acc[`${key}_visitados`] = (acc[`${key}_visitados`] || 0) + (a[`${key}_visitados`] || 0);
      });
      acc.clientes_asignados += a.clientes_asignados || 0;
      acc.clientes_visitados_total += a.clientes_visitados_total || 0;
      return acc;
    },
    { clientes_asignados: 0, clientes_visitados_total: 0 }
  );
  const totalSinVisitar = totales.clientes_asignados - totales.clientes_visitados_total;

  return (
    <div className="crmcs-cot-tabla-wrap">
      <div className="crmcs-cot-tabla-scroll scroll">
        <table cellPadding={0} cellSpacing={0} className="rcc-tabla">
          <thead>
            <tr>
              <th rowSpan={2}>
                <div>
                  <FiltroMultiple {...filtroProps("asesor", "Vendedor")} />
                </div>
              </th>
              <th className="rcc-th-vacio" rowSpan={2}>
                <div className="borde-izq" />
              </th>
              {SEGMENTOS.map((s) => (
                <th key={s.key}>
                  <div className="borde-izq">{s.label}</div>
                </th>
              ))}
              <th>
                <div className="borde-izq">Total</div>
              </th>
              <th rowSpan={2}>
                <div className="borde-izq">Sin visitar</div>
              </th>
              <th rowSpan={2}>
                <div className="borde-izq">% Cobertura</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {listado.map((a) => {
              const sinVisitar = (a.clientes_asignados || 0) - (a.clientes_visitados_total || 0);
              const coberturaAsesor = porcentaje(a.clientes_visitados_total, a.clientes_asignados);
              return (
                <React.Fragment key={a.idasesor}>
                  <tr className="rcc-row-asignada">
                    <td className="rca-td-asesor rcc-td-vendedor" rowSpan={3}>
                      <strong>{a.asesor}</strong>
                    </td>
                    <td className="rcc-td-etiqueta">Cartera asignada</td>
                    {SEGMENTOS.map((s) => (
                      <td key={s.key}>{a[`${s.key}_asignados`]}</td>
                    ))}
                    <td className="rcc-td-total">{a.clientes_asignados}</td>
                    <td className="rcc-td-sinvisitar" rowSpan={3}>{sinVisitar}</td>
                    <td className="rcc-td-cobertura" rowSpan={3}>{coberturaAsesor}</td>
                  </tr>
                  <tr className="rcc-row-visitada">
                    <td className="rcc-td-etiqueta">Visitada</td>
                    {SEGMENTOS.map((s) => (
                      <td key={s.key}>{a[`${s.key}_visitados`]}</td>
                    ))}
                    <td className="rcc-td-total">{a.clientes_visitados_total}</td>
                  </tr>
                  <tr className="rcc-row-porcentaje">
                    <td className="rcc-td-etiqueta">%</td>
                    {SEGMENTOS.map((s) => (
                      <td key={s.key} className="rcc-td-porcentaje">
                        {porcentaje(a[`${s.key}_visitados`], a[`${s.key}_asignados`])}
                      </td>
                    ))}
                    <td className="rcc-td-porcentaje rcc-td-total">
                      {porcentaje(a.clientes_visitados_total, a.clientes_asignados)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
            <tr className="crmcs-fila-total-sticky">
              <td colSpan={2}>Total ({listado.length} asesores)</td>
              {SEGMENTOS.map((s) => (
                <td key={s.key}>{totales[`${s.key}_asignados`]}</td>
              ))}
              <td>{totales.clientes_asignados}</td>
              <td>{totalSinVisitar}</td>
              <td>{porcentaje(totales.clientes_visitados_total, totales.clientes_asignados)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
